import type {
    KpiCardData,
    KpiCardDataRequest,
    KpiCardState,
    KpiForm,
    KpiMetric,
    KpiMultilineUnit,
    KpiSource,
    KpiSourceDeps,
    KpiSplitSide,
    KpiTrend,
} from '../types';
import type {
    KpiEnvelope,
    EnvelopeMetric,
    RegionGrain,
} from '~stores/kpi-envelope';
import { todayISO } from '../resolvers';
import { periodQuery, servedPeriod } from '../periods';
import { FORM_DEFINITIONS } from '../form-definitions';
import { notesForMetric } from './warning-scope';
import {
    warnOnRegionGrainDrift,
    PRE_PRODUCTION_WARN_ENVS,
} from './region-grain-drift';

export interface EnvelopeMetricConfig<M extends KpiMetric = KpiMetric> {
    id: M;
    label: string;
    type: 'scalar' | 'ratio';
    /** Display hint for a `scalar` that is a duration in seconds. The `number`
     * form renders it as a compact `2m 18s` instead of a bare `137.5`, matching
     * the Ask AI tiles' `duration_seconds` formatting. */
    isDuration?: boolean;
    /** When true, a lower value is better (error rate, failed sessions, churn),
     * so the trend colour is inverted: a decrease reads green, an increase red.
     * Threaded onto the card's `trend.invert`. Omitted = higher is better. */
    lowerIsBetter?: boolean;
    /** Display hint for a figure rendered as currency: a `split` card's side
     * and a `rate-trio`'s three windows are prefixed with `$` instead of
     * reading as a bare count. The `number` form has no `isCurrency`. */
    isCurrency?: boolean;
}

/** One side of a split card. With `metricSuffix`, the side reads
 * `${metricId}${metricSuffix}` and renders unavailable when that keyed metric is
 * absent or its type does not match the base metric (a cohort not yet sourced).
 * Without it, the side reads the base metric (a scalar count, or a ratio rendered
 * as a percent) and is available unless a ratio base has a null rate. Both sides
 * carry a trend when the cohort has compare data. */
export interface EnvelopeSplitSideConfig {
    label: string;
    metricSuffix?: string;
    /** When set, this side reads this exact metric id from the envelope
     * (scalar or ratio), independent of the card base metric. Use for a split
     * of two distinct metrics (e.g. couponsIssued vs couponsRedeemed) rather
     * than one metric across two cohorts. Takes precedence over metricSuffix.
     * The currency/sentiment display flags (isCurrency, lowerIsBetter) are read
     * from this metric's own config, so a metricId side renders with its own
     * display semantics rather than the base metric's. */
    metricId?: string;
}

/** Config for the `split` form. Either side can be the available (base-metric)
 * cohort; the other names a `metricSuffix` for the pending cohort, or a
 * `metricId` to read a distinct metric outright. */
export interface EnvelopeSplitConfig {
    left: EnvelopeSplitSideConfig;
    right: EnvelopeSplitSideConfig;
}

export interface EnvelopeSourceConfig<M extends KpiMetric = KpiMetric> {
    label: string;
    /** API path under /api-cms (e.g. 'analytics/age-gating/acquisition'). */
    endpoint: string;
    metrics: readonly EnvelopeMetricConfig<M>[];
    forms: readonly KpiForm[];
    /** The finest region grain this source's endpoint actually resolves. Gates
     * whether a selected region is forwarded as `provinces` (a non-empty region
     * is forwarded unless the grain is `national`, since a national endpoint
     * ignores it) and is checked in development against the returned envelope's
     * own `capabilities.regionGrain` (see the drift guard in `getCardData`), so
     * the CMS's assumption about a KPI's region resolution can never silently
     * drift from what the API actually serves. Required (not optional): every
     * source must state its grain honestly, and the compiler enforces it. */
    regionGrain: RegionGrain;
    /** Required when `forms` includes `split`. */
    split?: EnvelopeSplitConfig;
    /** Query params this source pins on every request, for an endpoint that
     * takes one outside the shared envelope contract (the top-redeemed
     * ranking's `limit`). Merged under the contract params, so a source can
     * never shadow `startDate`, `endDate` or `granularity`. `provinces` and the
     * compare dates are written conditionally, so a static key of those names
     * WOULD survive when the condition is false. Omitted by every source whose
     * endpoint takes only the standard set. */
    staticParams?: Readonly<Record<string, string>>;
    /** Set when the endpoint honours `live=1` (compute now rather than serve
     * the stored figures), which turns on the card's "Live data" button. */
    liveCapable?: boolean;
    /** When set, the `bars` form plots these named scalar metrics as bars (e.g.
     * a two-stage cohort funnel: registered vs profileCompleted), in the given
     * order, instead of reading a dimensional breakdown axis. Each entry pairs a
     * bar label with the scalar metric id to read. Mirrors `columnsMetrics`
     * below, for the `bars` form. Sources without it keep the
     * breakdown-axis `bars` behavior unchanged. */
    barsMetrics?: readonly { label: string; metricId: NoInfer<M> }[];
    /** Pairs with `barsMetrics` when every bar's share is of a shared
     * denominator (e.g. six onboarding states, each a share of `registered`)
     * rather than of the tallest configured bar. When set, the branch reads
     * this scalar once and requires it present/scalar/non-null, since it is
     * load-bearing for every bar: a missing, non-scalar, or null base errors
     * the whole card. A zero base does not: an empty cohort has every state at
     * 0 too, so every bar is emitted at `rate: 0` and the card still renders,
     * keeping an empty period distinguishable from a broken contract
     * (`columnsBaseMetricId` errors on zero instead). Each value-bearing bar is
     * then emitted as
     * `{ label, value, rate: value/base, base }`, putting the `bars` form
     * into the same rate mode its dimensional/breakdown rows already use. A
     * state metric declared unavailable (null) still yields its unavailable
     * step, excluded from the rate computation, same as without a base. */
    barsBaseMetricId?: NoInfer<M>;
    /** When set, the `columns` form plots these named scalar metrics as columns
     * (e.g. available vs reclaimed), in the given order, instead of reading a
     * funnel breakdown. Each entry pairs a column label with the scalar metric id
     * to read. Sources without it keep the funnel-columns behavior unchanged. */
    columnsMetrics?: readonly { label: string; metricId: NoInfer<M> }[];
    /** For a `columnsMetrics` card: read this scalar once and print every
     * column as a share of it (`38 · 100%`, `2 · 5%`) instead of a bare count.
     * Computed entirely from metrics the envelope already carries, so it costs
     * the API nothing. Load-bearing like `barsBaseMetricId`: a missing,
     * non-scalar, null or zero base errors the whole card, since every share
     * would be undefined: the base is load-bearing for every column at once,
     * unlike a single column's own null, which only costs that column.
     * Emitted as `share` rather than `rate` on purpose: `rate` would flip the
     * form to percentage-only bars, and this reading keeps both numbers. */
    columnsBaseMetricId?: NoInfer<M>;
    /** For `columns`: a plain dimensional axis (one column per row), unlike
     * the funnel below. Cell names, not metric ids, since a row's own count
     * can differ from the envelope's own scalar. Others are unaffected. */
    columnsAxisCells?: {
        valueCell: string;
        subLabelCell?: string;
        /** Guards a card pinned to one metric among several on this source: a
         * mismatched resolved metric errors before reading the axis, instead
         * of misreading another metric's rows. */
        forMetric?: NoInfer<M>;
    };
    /** Required when the `multiline` form's resolved metric is a `ratio`: the
     * row-level scalar metric ids whose ratio is each breakdown row's own
     * LEGEND value (a business unit's period rate), e.g. age-gating's
     * `{ numeratorMetricId: 'ageGatedUsers', denominatorMetricId: 'newUsers'
     * }`. The legend is computed from those counts, whether or not the row
     * also carries a rate cell. It must NOT be derived by averaging the row's
     * daily rate points: a business unit's zero-new-user days would pull a
     * real period rate toward 0, and the per-row count totals exist precisely
     * so the mapping does not have to. Unused when the resolved metric is a scalar
     * (the legend then reads the row's own cell directly, as `pie`/`bars` do). */
    multilineRate?: {
        numeratorMetricId: NoInfer<M>;
        denominatorMetricId: NoInfer<M>;
    };
    /** When set, the `multiline` form draws one line per named metric off the
     * envelope's top-level series (several measures, not one measure split by
     * an attribute). Sources without it keep the breakdown-axis behavior. */
    multilineMetrics?: readonly { label: string; metricId: NoInfer<M> }[];
    /** When true, every request pins `endDate` to today, ignoring the card's
     * resolved end date (its start still follows the period/page filter). For
     * sources whose signal only counts correctly for a window ending now, e.g.
     * the DB-login active-users source: `last_login_at` keeps one row per user,
     * so a window ending in the past under-counts to just the users who went
     * dormant during it. */
    anchorEndToday?: boolean;
}

/** Compute trend from compare data for number (scalar) and rate (ratio) metrics.
 * The delta is the ABSOLUTE change in the metric's own unit (dollars for a
 * currency metric, seconds for a duration metric, a plain count otherwise,
 * percentage-points for a rate), not a relative percent. `lowerIsBetter` flips
 * the trend colour downstream (see `KpiTrend.invert`); `isCurrency` selects the
 * `currency` display unit and `isDuration` the `duration` one (mutually
 * exclusive, so the delta reads in the same unit as the value above it). */
const computeTrend = (
    metric: EnvelopeMetric,
    form: 'number' | 'rate',
    comparisonLabel: string,
    lowerIsBetter?: boolean,
    isCurrency?: boolean,
    isDuration?: boolean
): KpiTrend | undefined => {
    let delta: number;
    let unit: KpiTrend['unit'];

    if (form === 'number') {
        if (!metric.compare) return undefined;
        // A number card is fed either a scalar (its `value`) or a ratio whose
        // `rate` is the displayed average (e.g. avg session length). The trend
        // is the absolute change in that number, in its own unit: dollars for a
        // currency metric, otherwise a plain count. A zero prior period needs
        // no special case: the change is just the current value.
        const current = metric.type === 'ratio' ? metric.rate : metric.value;
        const compare =
            metric.type === 'ratio'
                ? metric.compare.rate
                : metric.compare.value;
        if (current == null || compare == null) return undefined;
        delta = current - compare;
        unit = isCurrency ? 'currency' : isDuration ? 'duration' : 'count';
    } else {
        if (metric.type !== 'ratio' || !metric.compare) return undefined;
        // rate form: requires both rates to be non-null
        if (metric.rate == null || metric.compare.rate == null)
            return undefined;
        // The change in percentage points (rate is a 0..1 share), so
        // 0.84% -> 6.2% reads "+5.36%". This stays within +/-100 points and a
        // zero prior rate just yields the current rate's point value.
        delta = (metric.rate - metric.compare.rate) * 100;
        unit = 'percent';
    }

    const direction: KpiTrend['direction'] =
        delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';

    return {
        delta,
        unit,
        direction,
        comparisonLabel,
        ...(lowerIsBetter ? { invert: true } : {}),
    };
};

/**
 * Read a metric cell as a finite number. A cell is contractually `number | string
 * | null` (see `MetricCell`); a missing or non-numeric cell (e.g. a `"N/A"` string)
 * coerces to 0 rather than `NaN`. This keeps a single bad cell from poisoning the
 * shared max/total in the bars/pie helpers (one `NaN` there collapses the whole
 * chart to 0%), and matches the schema's documented "missing cell -> flat/zero"
 * degradation.
 */
const finiteCell = (cell: unknown): number => {
    const n = Number(cell ?? 0);
    return Number.isFinite(n) ? n : 0;
};

/**
 * A metric cell as a nullable number, WITHOUT `finiteCell`'s 0-default: a
 * `multiline` point or legend preserves an absent/non-numeric/null cell as a
 * genuine gap (the chart draws it with `connectNulls={false}`), never a false
 * zero drawn over a business unit that had nothing to report that day.
 */
const nullableCell = (cell: unknown): number | null =>
    typeof cell === 'number' ? cell : null;

/**
 * Rescale a `multiline` value for its unit: `percent` multiplies onto the
 * 0..100 scale `formatPercentValue` expects (matching `sideFromMetric`'s
 * `rate * 100` above); `count` passes a plain number through unchanged.
 * `null` (no data for this point/row) is never scaled into a false zero.
 */
const scaleForUnit = (
    value: number | null,
    unit: KpiMultilineUnit
): number | null =>
    value === null ? null : unit === 'percent' ? value * 100 : value;

/**
 * A breakdown row's own period rate, from its raw count cells, never from
 * averaging its daily rate points (see `EnvelopeSourceConfig.multilineRate`'s
 * doc comment: the row's `series` carries a rate PER DAY, which is a
 * different number from the rate over the whole period). `null` on a zero
 * denominator, matching the API's own `computeRate`, rather than a
 * divide-by-zero `NaN`.
 */
const rowPeriodRate = (
    row: Record<string, unknown>,
    ids: { numeratorMetricId: string; denominatorMetricId: string }
): number | null => {
    const denominator = finiteCell(row[ids.denominatorMetricId]);
    return denominator === 0
        ? null
        : finiteCell(row[ids.numeratorMetricId]) / denominator;
};

/**
 * The envelope's completeness boundary, but only where it clips days off the
 * requested window: at or past `range.endDate` it took nothing, and before
 * `range.startDate` it leaves a covered window with no days to name.
 */
const clippedCompleteThrough = (env: KpiEnvelope): string | undefined =>
    env.dataCompleteThrough !== undefined &&
    env.dataCompleteThrough >= env.range.startDate &&
    env.dataCompleteThrough < env.range.endDate
        ? env.dataCompleteThrough
        : undefined;

/** One line per configured metric, off the envelope's top-level `series`
 * instead of a breakdown axis. `null` where the envelope cannot back the
 * lines, which the caller renders as an error tile. */
const multilineFromMetricList = (
    env: KpiEnvelope,
    configured: readonly { label: string; metricId: string }[]
): KpiCardData | null => {
    const points = env.series;
    // An empty series would draw every line empty under a real legend value,
    // so it degrades like an absent one.
    if (!points?.length || configured.length === 0) {
        return null;
    }

    const lines: {
        label: string;
        metricId: string;
        metric: EnvelopeMetric;
    }[] = [];
    for (const entry of configured) {
        const metric = env.metrics[entry.metricId];
        // `nullableCell` reads a missing cell as null, so a metric absent from
        // any row would draw a gap the legend still counts.
        // `metrics` proves the KPI computes it, only the points prove it has a
        // daily curve: absent from EVERY point draws one line empty under a
        // real legend value, where one absent day stays an honest gap.
        if (!metric || points.every((point) => !(entry.metricId in point))) {
            return null;
        }
        lines.push({ ...entry, metric });
    }
    const [{ metric: first }] = lines;
    // One axis, so one scale: a count drawn beside a rate would be rescaled
    // onto the percent scale as though it were a share.
    if (lines.some((line) => line.metric.type !== first.type)) {
        return null;
    }
    const unit: KpiMultilineUnit =
        first.type === 'ratio' ? 'percent' : 'count';

    const completeThrough = clippedCompleteThrough(env);

    return {
        form: 'multiline',
        range: { start: env.range.startDate, end: env.range.endDate },
        ...(completeThrough !== undefined && { completeThrough }),
        unit,
        series: lines.map(({ label, metricId, metric }) => ({
            label,
            value: scaleForUnit(
                metric.type === 'ratio' ? metric.rate : metric.value,
                unit
            ),
            points: points.map((point) => ({
                date: point.date,
                value: scaleForUnit(nullableCell(point[metricId]), unit),
            })),
        })),
    };
};

/**
 * Build a KPI source from an endpoint that speaks the standardized envelope.
 * Adding a KPI becomes pure config: no bespoke fetch or mapping code. The card's
 * `form` selects how the chosen metric is read (number / rate / line); the
 * granularity it needs is stated once in `FORM_DEFINITIONS`, which every request
 * builder reads.
 *
 * `M` carries the source's exact metric ids OUT to `KpiSource<M>`, which is what
 * makes a mispaired card a compile error rather than a grey tile. Two details do
 * that work: `const M` keeps the ids as literals instead of widening them to
 * `string`, and `M extends KpiMetric` stops a source from inventing an id the
 * catalog does not know (`'ageVerified'` was exactly that, declared here and
 * absent from `KpiMetric`, compiling because the bound used to be `string`).
 * Returning the erased `KpiSource` threw all of it away at the last step.
 */
export const createEnvelopeSource = <const M extends KpiMetric>(
    config: EnvelopeSourceConfig<M>
): KpiSource<M> => {
    // A config precondition (e.g. split needs config.split), checked once
    // here instead of erroring per-card at render. Says nothing about what a
    // card passes, so it cannot catch a card/source mismatch (bars stays []).
    for (const form of config.forms) {
        for (const key of FORM_DEFINITIONS[form].requires) {
            if (config[key] == null) {
                throw new Error(
                    `${config.label}: the "${form}" form requires "${key}" on the source config.`
                );
            }
        }
    }

    // `barsBaseMetricId` is only read by the named-scalar bars branch, so
    // setting it without `barsMetrics` would silently do nothing. Same
    // precondition style as the form requirements above.
    if (config.barsBaseMetricId && !config.barsMetrics) {
        throw new Error(
            `${config.label}: "barsBaseMetricId" requires "barsMetrics" on the source config.`
        );
    }
    if (config.multilineMetrics && config.multilineRate) {
        throw new Error(
            `${config.label}: "multilineRate" is never read beside "multilineMetrics" on the source config.`
        );
    }
    if (config.multilineMetrics?.length === 0) {
        throw new Error(
            `${config.label}: "multilineMetrics" requires at least one metric on the source config.`
        );
    }
    if (
        config.multilineMetrics &&
        new Set(config.multilineMetrics.map((entry) => entry.label)).size !==
            config.multilineMetrics.length
    ) {
        throw new Error(
            `${config.label}: "multilineMetrics" entries must have distinct labels on the source config.`
        );
    }

    const defaultMetric = config.metrics[0]?.id;
    const resolveMetric = (metric: string | undefined): string =>
        metric ?? defaultMetric;

    const toCardData = (
        env: KpiEnvelope,
        req: KpiCardDataRequest
    ): KpiCardState => {
        const metricId = resolveMetric(req.metric);
        const metricConfig = config.metrics.find((m) => m.id === metricId);
        const lowerIsBetter = metricConfig?.lowerIsBetter;
        const isCurrency = metricConfig?.isCurrency;
        const isDuration = metricConfig?.isDuration;

        if (env.degradation) {
            return {
                status: 'unavailable',
                reason: env.degradation.reason,
                ...(env.degradation.cause !== undefined && {
                    cause: env.degradation.cause,
                }),
            };
        }

        // The envelope's notes that bear on this card's metric surface in the
        // (i) popover, and a `warning` among them colours it. Omitted rather
        // than empty when none does, like `series` and `degradation`.
        const scoped = notesForMetric(env.kpi, metricId, env.caveats);
        const notes = scoped.length > 0 ? { caveats: scoped } : undefined;
        const ready = (data: KpiCardData): KpiCardState => ({
            status: 'ready',
            data,
            ...notes,
            ...(env.computedAt !== undefined && {
                computedAt: env.computedAt,
            }),
            // Dates sent are dates served; only a preset leaves them to the API.
            ...(req.period.preset && { served: servedPeriod(env) }),
        });

        if (req.form === 'rate') {
            const metric = env.metrics[metricId];
            if (!metric || metric.type !== 'ratio') {
                return { status: 'error' };
            }
            const trend = req.compare
                ? computeTrend(metric, 'rate', req.compare.label, lowerIsBetter)
                : undefined;
            return ready({
                form: 'rate',
                numerator: metric.numerator,
                denominator: metric.denominator,
                rate: metric.rate,
                ...(trend !== undefined && { trend }),
            });
        }

        if (req.form === 'line') {
            // Narrow like rate/number: a dropped/unknown metric (absent from the
            // envelope's metrics record) is an error tile, not a flat-zero line.
            if (!env.metrics[metricId]) {
                return { status: 'error' };
            }
            if (!env.series) {
                return { status: 'pending' };
            }
            // `metrics` proves the KPI computes the metric; only the rows prove it
            // has a daily curve. A row without the cell reads 0 through
            // `finiteCell`, so a metric missing from any row is an error tile.
            if (env.series.some((point) => !(metricId in point))) {
                return { status: 'error' };
            }
            return ready({
                form: 'line',
                points: env.series.map((point) => ({
                    date: point.date,
                    value: finiteCell(point[metricId]),
                })),
            });
        }

        // A region axis as N labelled lines (e.g. age-gating's four
        // business-unit rates): reads the plural `breakdowns`, one line per
        // row, each drawn from that row's own per-day `series` (#765).
        if (req.form === 'multiline') {
            if (config.multilineMetrics) {
                // Dev/staging only: both fields are dropped silently here,
                // which construction cannot see. Every SHIPPED card is caught
                // in CI instead, by `card-catalog-integrity.test.ts`.
                const ignored = [
                    ...(req.breakdownBy
                        ? [`breakdownBy "${req.breakdownBy}"`]
                        : []),
                    ...(req.metric ? [`metric "${req.metric}"`] : []),
                ];
                if (
                    ignored.length > 0 &&
                    PRE_PRODUCTION_WARN_ENVS.includes(
                        import.meta.env.REACT_APP_ENV
                    )
                ) {
                    console.warn(
                        `KpiSource "${config.label}": card requested ${ignored.join(' and ')} but multilineMetrics is configured, so the source's own metric list is drawn instead.`
                    );
                }
                const data = multilineFromMetricList(
                    env,
                    config.multilineMetrics
                );
                return data === null ? { status: 'error' } : ready(data);
            }
            // Narrow like line/pie/bars: a dropped/unknown metric is an error
            // tile, not a chart with no legend.
            const metric = env.metrics[metricId];
            if (!metric) {
                return { status: 'error' };
            }
            const axis = env.breakdowns.find((b) => b.by === req.breakdownBy);
            if (!axis) {
                return { status: 'error' };
            }
            // Rule 11 of the envelope contract: within one breakdown, either
            // every row carries a series or none does. A partial set would
            // silently draw a subset of lines on a chart expecting all of
            // them, so a mixed shape is an error tile, the same discipline
            // the `bars` rate-mode guard above applies to `rate`/`base`.
            if (!axis.rows.every((row) => row.series !== undefined)) {
                return { status: 'error' };
            }
            // A ratio metric (age-gating's ageGatedRate) reads as a 0..100
            // percent, matching KpiSplitSide's live convention
            // (`sideFromMetric`'s `rate * 100` above); a scalar metric reads
            // as a plain count, like a `pie`/`bars` cell.
            const unit: KpiMultilineUnit =
                metric.type === 'ratio' ? 'percent' : 'count';
            // A ratio metric's row-level LEGEND value needs the row's own
            // count cells (see `multilineRate`'s doc comment); a source
            // declaring `multiline` with a ratio metric and no
            // `multilineRate` configured cannot compute it, a config bug
            // rather than a card this branch can render.
            if (metric.type === 'ratio' && !config.multilineRate) {
                return { status: 'error' };
            }
            const rateIds = config.multilineRate;
            const series = axis.rows.map((row) => ({
                label: row.label,
                value: scaleForUnit(
                    metric.type === 'ratio' && rateIds
                        ? rowPeriodRate(row, rateIds)
                        : nullableCell(row[metricId]),
                    unit
                ),
                points: (row.series ?? []).map((point) => ({
                    date: point.date,
                    value: scaleForUnit(nullableCell(point[metricId]), unit),
                })),
            }));
            const completeThrough = clippedCompleteThrough(env);
            return ready({
                form: 'multiline',
                range: { start: env.range.startDate, end: env.range.endDate },
                ...(completeThrough !== undefined && { completeThrough }),
                unit,
                series,
            });
        }

        if (req.form === 'split') {
            if (!config.split) {
                return { status: 'error' };
            }
            const base = env.metrics[metricId];
            // Split cards read a scalar count or a ratio rate base; a missing
            // base metric is an error tile, not a zero.
            if (!base || (base.type !== 'scalar' && base.type !== 'ratio')) {
                return { status: 'error' };
            }

            // Render any scalar/ratio metric as a split side, applying that
            // side's own currency/sentiment flags: the base side passes the base
            // metric's, a metricId side passes its own metric config's, so each
            // side renders with its own display semantics.
            const sideFromMetric = (
                label: string,
                metric: EnvelopeMetric | undefined,
                flags: { isCurrency?: boolean; lowerIsBetter?: boolean }
            ): KpiSplitSide => {
                if (
                    !metric ||
                    (metric.type !== 'scalar' && metric.type !== 'ratio')
                ) {
                    return { label, unavailable: true };
                }
                if (metric.type === 'ratio') {
                    if (metric.rate == null) {
                        return { label, unavailable: true };
                    }
                    const trend = req.compare
                        ? computeTrend(
                              metric,
                              'rate',
                              req.compare.label,
                              flags.lowerIsBetter
                          )
                        : undefined;
                    return {
                        label,
                        value: metric.rate * 100,
                        unit: 'percent',
                        ...(trend !== undefined && { trend }),
                    };
                }
                // A declared-unavailable scalar (null value): same treatment as
                // a null ratio rate above, not a missing side.
                if (metric.value == null) {
                    return { label, unavailable: true };
                }
                const trend = req.compare
                    ? computeTrend(
                          metric,
                          'number',
                          req.compare.label,
                          flags.lowerIsBetter,
                          flags.isCurrency
                      )
                    : undefined;
                return {
                    label,
                    value: metric.value,
                    ...(flags.isCurrency && { isCurrency: true }),
                    ...(trend !== undefined && { trend }),
                };
            };

            // The available (no-suffix) side always mirrors the base metric.
            // A ratio base renders as a percent (rate * 100, matching
            // formatPercentValue's 0-100 scale); a null rate is unavailable.
            const availableSide = (label: string): KpiSplitSide =>
                sideFromMetric(label, base, { isCurrency, lowerIsBetter });

            const resolveSide = (
                sideConfig: EnvelopeSplitSideConfig
            ): KpiSplitSide => {
                if (sideConfig.metricId) {
                    const sideMetricConfig = config.metrics.find(
                        (m) => m.id === sideConfig.metricId
                    );
                    return sideFromMetric(
                        sideConfig.label,
                        env.metrics[sideConfig.metricId],
                        {
                            isCurrency: sideMetricConfig?.isCurrency,
                            lowerIsBetter: sideMetricConfig?.lowerIsBetter,
                        }
                    );
                }
                if (sideConfig.metricSuffix) {
                    const cohort =
                        env.metrics[`${metricId}${sideConfig.metricSuffix}`];
                    if (!cohort || cohort.type !== base.type) {
                        return { label: sideConfig.label, unavailable: true };
                    }
                    if (cohort.type === 'ratio') {
                        if (cohort.rate == null) {
                            return {
                                label: sideConfig.label,
                                unavailable: true,
                            };
                        }
                        const trend = req.compare
                            ? computeTrend(
                                  cohort,
                                  'rate',
                                  req.compare.label,
                                  lowerIsBetter
                              )
                            : undefined;
                        return {
                            label: sideConfig.label,
                            value: cohort.rate * 100,
                            unit: 'percent',
                            ...(trend !== undefined && { trend }),
                        };
                    }
                    // A declared-unavailable cohort scalar (null value): same
                    // treatment as a null cohort rate above, not a missing side.
                    if (cohort.value == null) {
                        return {
                            label: sideConfig.label,
                            unavailable: true,
                        };
                    }
                    const cohortTrend = req.compare
                        ? computeTrend(
                              cohort,
                              'number',
                              req.compare.label,
                              lowerIsBetter,
                              isCurrency
                          )
                        : undefined;
                    return {
                        label: sideConfig.label,
                        value: cohort.value,
                        ...(metricConfig?.isCurrency && {
                            isCurrency: true,
                        }),
                        ...(cohortTrend !== undefined && {
                            trend: cohortTrend,
                        }),
                    };
                }
                return availableSide(sideConfig.label);
            };

            return ready({
                form: 'split',
                left: resolveSide(config.split.left),
                right: resolveSide(config.split.right),
            });
        }

        // Dimensional breakdown charts: bars/pie read the plural `breakdowns`
        // via `breakdownBy`. A `columns` card is always an ordered funnel and is
        // handled just below (it reads a single funnel, not a per-metric axis).
        if (req.form === 'pie') {
            // Narrow like rate/number/line: a dropped/unknown metric (absent from
            // the envelope's metrics record) is an error tile, not a flat-zero chart
            // built from `Number(row[metricId] ?? 0)`.
            if (!env.metrics[metricId]) {
                return { status: 'error' };
            }
            const axis = env.breakdowns.find((b) => b.by === req.breakdownBy);
            if (!axis) {
                return { status: 'error' };
            }
            const slices = axis.rows.map((row) => ({
                label: row.label,
                value: finiteCell(row[metricId]),
            }));
            return ready({ form: 'pie', slices, palette: req.palette });
        }

        if (req.form === 'bars') {
            // Named-scalar bars (e.g. a two-stage cohort funnel: registered vs
            // profileCompleted): when the source configures `barsMetrics`, each
            // bar reads one scalar metric by id, in order, instead of a
            // breakdown axis. A configured metric that is absent or not scalar
            // is an error tile (parity with the columnsMetrics branch), not a
            // silent zero bar. A present scalar with a `null` value is a
            // declared unavailability (its source table does not exist yet for
            // this window), not a typo: only that bar renders unavailable while
            // its siblings render normally. `barsBaseMetricId` (optional)
            // additionally shares every bar's denominator, putting the form
            // into rate mode; see its own doc comment on `EnvelopeSourceConfig`.
            if (config.barsMetrics) {
                // Dev/staging only: a card also naming breakdownBy here has
                // it dropped below, silently, which construction cannot see
                // (the source has no way to know which cards name it).
                if (
                    req.breakdownBy &&
                    PRE_PRODUCTION_WARN_ENVS.includes(
                        import.meta.env.REACT_APP_ENV
                    )
                ) {
                    console.warn(
                        `KpiSource "${config.label}": card requested breakdownBy "${req.breakdownBy}" but barsMetrics is configured, so the axis is ignored.`
                    );
                }
                // `barsBaseMetricId`: every bar's share is of this one shared
                // denominator (e.g. six onboarding states, each a share of
                // `registered`), not of the tallest configured bar. The base
                // is load-bearing for every row, so it is resolved once,
                // up front, and any failure to resolve it errors the whole
                // card rather than a per-row unavailable state.
                let base: number | undefined;
                if (config.barsBaseMetricId) {
                    const baseMetric = env.metrics[config.barsBaseMetricId];
                    if (
                        !baseMetric ||
                        baseMetric.type !== 'scalar' ||
                        baseMetric.value == null
                    ) {
                        return { status: 'error' };
                    }
                    base = finiteCell(baseMetric.value);
                }

                const steps: (
                    | { label: string; value: number; rate?: number; base?: number }
                    | { label: string; unavailable: true }
                )[] = [];
                for (const { label, metricId } of config.barsMetrics) {
                    const metric = env.metrics[metricId];
                    if (!metric || metric.type !== 'scalar') {
                        return { status: 'error' };
                    }
                    if (metric.value == null) {
                        steps.push({ label, unavailable: true });
                        continue;
                    }
                    const value = finiteCell(metric.value);
                    steps.push(
                        base !== undefined
                            ? {
                                  label,
                                  value,
                                  // A zero base is an empty cohort, where every
                                  // state is 0 too: 0 of 0 reads as 0%, the
                                  // honest share, and the card keeps rendering
                                  // its bars instead of an error tile.
                                  rate: base === 0 ? 0 : value / base,
                                  base,
                              }
                            : { label, value }
                    );
                }
                return ready({ form: 'bars', steps, base });
            }

            // Narrow like rate/number/line: a dropped/unknown metric (absent from
            // the envelope's metrics record) is an error tile, not a flat-zero chart
            // built from `Number(row[metricId] ?? 0)`.
            if (!env.metrics[metricId]) {
                return { status: 'error' };
            }
            const axis = env.breakdowns.find((b) => b.by === req.breakdownBy);
            if (!axis) {
                return { status: 'error' };
            }
            // Rate mode is a per-list property: if any row carries a `rate`, the
            // list is a rate list and every row must carry both `rate` and `base`.
            // A row missing either would silently drop to a `{ label, value }`
            // funnel step and misrender its share (an unclamped `0 · 0%`), so a
            // mixed shape is an error tile, not a partial render.
            const rateMode = axis.rows.some((row) => row.rate != null);
            if (
                rateMode &&
                !axis.rows.every((row) => row.rate != null && row.base != null)
            ) {
                return { status: 'error' };
            }
            let shareBase: number | undefined;
            if (req.breakdownBaseMetricId) {
                const baseMetric = env.metrics[req.breakdownBaseMetricId];
                if (!baseMetric || baseMetric.type !== 'scalar') {
                    return { status: 'error' };
                }
                const baseValue = finiteCell(baseMetric.value);
                if (baseValue > 0) {
                    shareBase = baseValue;
                }
            }
            const steps = axis.rows.map((row) => {
                const value = finiteCell(row[metricId]);
                if (shareBase !== undefined) {
                    return {
                        label: row.label,
                        value,
                        rate: value / shareBase,
                        base: shareBase,
                    };
                }
                // Rate mode: a row carrying both `rate` and `base` renders as an
                // absolute percentage against its base. Otherwise emit a plain
                // `{ label, value }` funnel step.
                if (row.rate != null && row.base != null) {
                    return {
                        label: row.label,
                        value,
                        rate: finiteCell(row.rate),
                        base: finiteCell(row.base),
                    };
                }
                return { label: row.label, value };
            });
            return ready({
                form: 'bars',
                steps,
                ...(shareBase !== undefined && { base: shareBase }),
            });
        }

        // Funnel columns: a `columns` card renders an ordered funnel, turning
        // each row's `reached` into a bar with a step-over-step delta, plus an
        // optional ratio headline. The funnel is the `breakdowns` entry the card
        // names via `breakdownBy` (the envelope emits the funnel there, e.g.
        // game-engagement-rpp's `by: 'round'`). Cells read through `finiteCell` so a
        // non-numeric cell renders as 0, not NaN.
        if (req.form === 'columns') {
            // Named-scalar columns (e.g. coupon available vs reclaimed): when the
            // source configures `columnsMetrics`, each column reads one scalar
            // metric by id, in order, instead of a funnel breakdown. A configured
            // metric absent from the envelope or not scalar errors the whole card:
            // that is a config typo, not a declared gap. A null value is the
            // declared gap, so it renders that one column unavailable and leaves
            // its siblings alone, same as the `bars` branch: the two cards read
            // the same metrics, so a window that costs `complete` its data must
            // not kill one card while the other degrades gracefully.
            if (config.columnsMetrics) {
                let columnsBase: number | undefined;
                if (config.columnsBaseMetricId) {
                    const baseMetric = env.metrics[config.columnsBaseMetricId];
                    if (
                        !baseMetric ||
                        baseMetric.type !== 'scalar' ||
                        baseMetric.value == null
                    ) {
                        return { status: 'error' };
                    }
                    columnsBase = finiteCell(baseMetric.value);
                    if (columnsBase === 0) {
                        return { status: 'error' };
                    }
                }

                const columns: (
                    | { label: string; value: number; share?: number }
                    | { label: string; unavailable: true }
                )[] = [];
                for (const { label, metricId } of config.columnsMetrics) {
                    const metric = env.metrics[metricId];
                    if (!metric || metric.type !== 'scalar') {
                        return { status: 'error' };
                    }
                    if (metric.value == null) {
                        columns.push({ label, unavailable: true });
                        continue;
                    }
                    const value = finiteCell(metric.value);
                    columns.push(
                        columnsBase !== undefined
                            ? { label, value, share: value / columnsBase }
                            : { label, value }
                    );
                }
                return ready({
                    form: 'columns',
                    columns,
                    // Card-specific footnote (opt-in): the funnel-columns branch
                    // below never sets it, so the retention card is unchanged.
                });
            }

            // A plain dimensional axis (e.g. the fifteen-rung visit-milestone
            // ladder), one column per row in row order: distinct from the
            // ordered funnel below, whose cells are fixed to `reached`/`dropRate`.
            if (config.columnsAxisCells) {
                const { valueCell, subLabelCell, forMetric } =
                    config.columnsAxisCells;
                if (forMetric && forMetric !== metricId) {
                    return { status: 'error' };
                }
                const axis = env.breakdowns.find(
                    (b) => b.by === req.breakdownBy
                );
                if (!axis) {
                    return { status: 'error' };
                }
                // A cell name no row carries at all is a config typo, not a
                // per-row gap: error the whole card instead of an all-N/A
                // one, mirroring the funnel's `reached` guard below.
                if (!axis.rows.some((row) => valueCell in row)) {
                    return { status: 'error' };
                }
                if (
                    subLabelCell &&
                    !axis.rows.some((row) => subLabelCell in row)
                ) {
                    return { status: 'error' };
                }
                const columns: (
                    | {
                          label: string;
                          value: number;
                          subLabel?: number;
                      }
                    | { label: string; unavailable: true }
                )[] = [];
                for (const row of axis.rows) {
                    const cell = row[valueCell];
                    if (cell == null) {
                        columns.push({ label: row.label, unavailable: true });
                        continue;
                    }
                    const subLabelValue = subLabelCell
                        ? row[subLabelCell]
                        : undefined;
                    columns.push({
                        label: row.label,
                        value: finiteCell(cell),
                        ...(typeof subLabelValue === 'number' && {
                            subLabel: subLabelValue,
                        }),
                    });
                }
                return ready({ form: 'columns', columns });
            }

            const funnel = env.breakdowns.find((b) => b.by === req.breakdownBy);
            if (!funnel) {
                return { status: 'error' };
            }
            const rows = funnel.rows;
            // A resolved funnel whose rows carry no `reached` cell is not a funnel
            // (e.g. a dimensional axis matched via `breakdownBy`, keyed by a metric
            // like `total`). Reading `reached` off those rows coerces every bar to 0
            // via `finiteCell`, so the card would render a silent all-zero funnel;
            // error instead.
            if (!rows.some((row) => 'reached' in row)) {
                return { status: 'error' };
            }
            // Rate mode (per-round drop-off): when the funnel rows carry a
            // `dropRate` cell, each bar is that round's own drop share as a
            // percentage (not a retention count), and the raw counts ride along
            // in a hover tooltip. A funnel without `dropRate` keeps the legacy
            // retention output (reached + step-over-step delta), so any non-drop
            // columns source is unaffected.
            const rateMode = rows.some((row) => 'dropRate' in row);
            // Rate mode is all-or-nothing, mirroring the `bars` branch: a funnel
            // where only some rows carry `dropRate` would silently render the
            // missing rows as `rate: null` (N/A) alongside real percentage bars,
            // a partial that reads as data rather than a gap. Error instead so a
            // mixed shape is an error tile, not a half-rendered funnel.
            if (rateMode && !rows.every((row) => 'dropRate' in row)) {
                return { status: 'error' };
            }
            const columns = rateMode
                ? rows.map((row) => {
                      const cell = row.dropRate;
                      const rate =
                          typeof cell === 'number' && Number.isFinite(cell)
                              ? cell
                              : null;
                      return {
                          label: row.label,
                          // The bar plots the round's own drop RATE as a
                          // percentage (rate * 100); the raw counts ride in the
                          // tooltip and the overall rate is the card headline. A
                          // null rate (nobody reached) renders a zero bar / N/A.
                          value: rate == null ? 0 : rate * 100,
                          rate,
                          tooltip: {
                              reached: finiteCell(row.reached),
                              dropped: finiteCell(row.abandoned),
                              won: finiteCell(row.wonHere),
                          },
                      };
                  })
                : rows.map((row, i) => {
                      const value = finiteCell(row.reached);
                      if (i === 0) {
                          return { label: row.label, value };
                      }
                      const prev = finiteCell(rows[i - 1].reached);
                      const delta =
                          prev === 0
                              ? 0
                              : Math.round(((value - prev) / prev) * 100);
                      return { label: row.label, value, delta };
                  });

            const metric = env.metrics[metricId];
            let headline: { rate: number | null; trend?: KpiTrend } | undefined;
            if (metric && metric.type === 'ratio') {
                const trend = req.compare
                    ? computeTrend(
                          metric,
                          'rate',
                          req.compare.label,
                          lowerIsBetter
                      )
                    : undefined;
                headline = {
                    rate: metric.rate,
                    ...(trend !== undefined && { trend }),
                };
            }

            return ready({
                form: 'columns',
                columns,
                ...(headline !== undefined && { headline }),
            });
        }

        // Fallthrough: `number`. Every other form is handled above; a scalar
        // metric renders as a number card, with an optional duration hint.
        const metric = env.metrics[metricId];
        if (!metric || (metric.type !== 'scalar' && metric.type !== 'ratio')) {
            return { status: 'error' };
        }
        // A ratio metric on a number card is an average carried as its
        // numerator/denominator (so it re-aggregates correctly across regions);
        // its `rate` is the number to show. A null rate (e.g. no sessions in the
        // period) is unavailable, not zero.
        const value = metric.type === 'ratio' ? metric.rate : metric.value;
        if (value == null) {
            return {
                status: 'unavailable',
                reason: 'No data for the selected period.',
            };
        }
        const trend = req.compare
            ? computeTrend(
                  metric,
                  'number',
                  req.compare.label,
                  lowerIsBetter,
                  isCurrency,
                  isDuration
              )
            : undefined;
        return ready({
            form: 'number',
            value,
            ...(metricConfig?.isDuration && { isDuration: true }),
            ...(metricConfig?.isCurrency && { isCurrency: true }),
            ...(trend !== undefined && { trend }),
        });
    };

    return {
        descriptor: {
            label: config.label,
            metrics: config.metrics.map((m) => ({ id: m.id, label: m.label })),
            forms: [...config.forms],
            regionGrain: config.regionGrain,
            multilineMetrics: config.multilineMetrics,
            ...(config.liveCapable && { liveCapable: true }),
        },

        async getCardData(
            req: KpiCardDataRequest,
            deps: KpiSourceDeps
        ): Promise<KpiCardState> {
            const { granularity } = FORM_DEFINITIONS[req.form];

            const params: Record<string, string> = {
                // Spread first: the contract params below win, so a source
                // pinning an extra param cannot shadow the window or the filter.
                ...config.staticParams,
                // `anchorEndToday` sources always run to today (their signal only
                // counts correctly for a window ending now); the start still
                // follows the card's resolved period.
                ...(config.anchorEndToday
                    ? { startDate: req.period.start, endDate: todayISO() }
                    : periodQuery(req.period)),
                granularity,
            };
            // A national endpoint ignores a province filter outright (it has no
            // region to narrow), so only forward `provinces` when the source's
            // declared grain is finer than national. Province/businessUnit/brand
            // sources all forward exactly as before; only a national source's
            // behavior changes (it now honestly sends nothing).
            if (config.regionGrain !== 'national' && req.region.length > 0) {
                params.provinces = req.region.join(',');
            }
            // Scope to a single entity when the card carries a selection (the
            // coupon-activation coupon picker). Omitted otherwise, so no other
            // source's request shape changes.
            if (req.promotionId) {
                params.promotionId = req.promotionId;
            }
            // Written after the `staticParams` spread: a card's campaign wins
            // over the one its source pins.
            if (req.campaignId) {
                params.campaignId = req.campaignId;
            }
            // Narrow to one customer group when the card carries a selection
            // (the Milestone rewards group selector). Omitted otherwise, so no
            // other source's request shape changes.
            if (req.segment) {
                params.segment = req.segment;
            }
            // A card's own row count wins over the source's pinned `limit`
            // (written after the spread above).
            if (req.limit != null) {
                params.limit = String(req.limit);
            }
            if (req.live) {
                params.live = '1';
            }
            if (req.compare && req.form !== 'line') {
                params.compare = '1';
            }

            // Forward the card's abort signal only when the caller provided one,
            // so a stale request can be dropped on a filter change. A plain
            // two-arg call otherwise, so callers that never abort are unchanged.
            const env = deps.signal
                ? await deps.analyticsStore.fetchKpiEnvelope(
                      config.endpoint,
                      params,
                      deps.signal
                  )
                : await deps.analyticsStore.fetchKpiEnvelope(
                      config.endpoint,
                      params
                  );
            if (!env) {
                return { status: 'error' };
            }
            // Catch a CMS/API drift on this KPI's region resolution before it
            // reaches production (see `warnOnRegionGrainDrift`).
            warnOnRegionGrainDrift(
                config.label,
                config.endpoint,
                config.regionGrain,
                env
            );
            return toCardData(env, req);
        },
    };
};
